import { useEffect, useRef, useState, type FormEvent } from "react";
import "./EmailDialog.css";

interface EmailDialogProps {
  open: boolean;
  /**
   * "portal" collects the email to send a billing-portal magic link.
   * "checkout" collects the email before redirecting to Stripe Checkout.
   */
  mode?: "portal" | "checkout";
  onCancel: () => void;
  onSubmit: (email: string) => void;
  errorMessage?: string;
  isLoading?: boolean;
  /** When true, the dialog shows the "check your email" success state. */
  isSent?: boolean;
  /** Overrides the default success-state copy (e.g. the already-subscribed case). */
  sentMessage?: string;
}

export function EmailDialog({
  open,
  mode = "portal",
  onCancel,
  onSubmit,
  errorMessage,
  isLoading = false,
  isSent = false,
  sentMessage,
}: EmailDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");

  // Sync the imperative <dialog> open/close API with the React `open` prop.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      setEmail("");
      requestAnimationFrame(() => inputRef.current?.focus());
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading || isSent) return;
    const trimmed = email.trim();
    if (trimmed) onSubmit(trimmed);
  };

  const isCheckout = mode === "checkout";
  const title = isSent
    ? "CHECK YOUR EMAIL"
    : isCheckout
      ? "START SUBSCRIPTION"
      : "MANAGE SUBSCRIPTION";
  const prompt = isCheckout
    ? "Enter your email to start your subscription. We'll take you to secure Stripe checkout next."
    : "Enter the email address on your subscription. We'll send you a one-time link to open the Stripe billing portal.";
  const submitLabel = isCheckout ? "CONTINUE" : "SEND LINK";

  return (
    <dialog
      ref={dialogRef}
      className="email-dialog"
      aria-labelledby="email-dialog-title"
      onClose={onCancel}
    >
      <form className="email-dialog__form" onSubmit={handleSubmit}>
        <h2 id="email-dialog-title" className="email-dialog__title">
          {title}
        </h2>

        {isSent ? (
          <>
            <p className="email-dialog__subtitle">
              {sentMessage ??
                "If that email matches a subscription, a link to open the Stripe billing portal is on its way. It expires in 15 minutes and can only be used once."}
            </p>
            <p
              className="email-dialog__subtitle email-dialog__subtitle--muted"
            >
              Check your spam folder if you don&apos;t see it.
            </p>
            <div className="email-dialog__actions">
              <button
                type="button"
                className="email-dialog__btn email-dialog__btn--submit"
                onClick={onCancel}
              >
                DONE
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="email-dialog__subtitle">{prompt}</p>

            <label
              className="email-dialog__label"
              htmlFor="email-dialog-email"
            >
              EMAIL
            </label>
            <input
              ref={inputRef}
              id="email-dialog-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="email-dialog__input"
              disabled={isLoading}
              aria-invalid={errorMessage ? true : undefined}
              aria-describedby={errorMessage ? "email-dialog-error" : undefined}
            />

            {errorMessage && (
              <p
                id="email-dialog-error"
                className="email-dialog__error"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div className="email-dialog__actions">
              <button
                type="button"
                className="email-dialog__btn email-dialog__btn--cancel"
                onClick={onCancel}
                disabled={isLoading}
              >
                CANCEL
              </button>
              <button
                type="submit"
                className="email-dialog__btn email-dialog__btn--submit"
                disabled={isLoading}
                aria-busy={isLoading}
              >
                {isLoading ? "PLEASE WAIT" : submitLabel}
              </button>
            </div>
          </>
        )}
      </form>
    </dialog>
  );
}
